// header_bar.jsx — top navigation for the Navela admin section.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, User, Shield, Ship, Route, Images, Newspaper, UserX, BarChart3, Menu, X } from 'lucide-react';
import { useAuth } from '../../authentication_components/authContext';
import navelaLogo from '../assets/navela-logo.png';

const NAV_ITEMS = [
  { id: 'vessels', label: 'Vessels', icon: <Ship className="h-4 w-4" /> },
  { id: 'ship_routes', label: 'Ship Routes', icon: <Route className="h-4 w-4" /> },
  { id: 'vessel_images', label: 'Vessel Images', icon: <Images className="h-4 w-4" /> },
  { id: 'articles', label: 'News Articles', icon: <Newspaper className="h-4 w-4" /> },
  { id: 'account_deletions', label: 'Deletion Requests', icon: <UserX className="h-4 w-4" /> },
  { id: 'metrics', label: 'Usage Metrics', icon: <BarChart3 className="h-4 w-4" /> },
];

export default function NavelaHeaderBar({ activeTab, onTabChange }) {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { user, loginType, logout } = useAuth();
  const navigate = useNavigate();

  const email = loginType === 'master' ? 'Master User' : user?.email || 'Admin User';
  const userTypeLabel = loginType === 'master' ? 'Master Administrator' : 'Administrator';

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      setUserDropdownOpen(false);
      setMobileMenuOpen(false);
      await logout();
    } catch {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  const selectTab = (id) => { onTabChange(id); setMobileMenuOpen(false); };

  return (
    <header className="sticky top-0 z-50 border-b border-pr-navy-100 bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Brand */}
          <button onClick={() => navigate('/portal')} className="flex flex-shrink-0 items-center gap-2.5" title="Back to portal">
            <img src={navelaLogo} alt="Navela" className="h-9 w-9 object-contain drop-shadow-sm" />
            <div className="text-left">
              <h1 className="text-lg font-extrabold tracking-tight text-pr-navy-800 sm:text-xl">Navela</h1>
              <p className="-mt-1 text-[10px] font-semibold uppercase tracking-widest text-pr-navy-400">Fleet Admin</p>
            </div>
            {loginType === 'master' && (
              <span className="ml-1 rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700">Master</span>
            )}
          </button>

          {/* Desktop tabs */}
          <nav className="hidden flex-1 justify-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => {
              const active = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition-all ${
                    active
                      ? 'border border-pr-navy-100 bg-pr-navy-50 text-pr-navy-700 shadow-sm'
                      : 'border border-transparent text-slate-500 hover:bg-slate-50 hover:text-pr-navy-600'
                  }`}
                >
                  <span className={active ? 'text-pr-navy-600' : 'text-slate-400'}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User + mobile toggle */}
          <div className="flex items-center gap-2">
            <div className="hidden text-right lg:block">
              <p className="text-sm font-semibold text-slate-700">{email}</p>
              <p className="text-xs text-slate-400">{userTypeLabel}</p>
            </div>
            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen((o) => !o)}
                disabled={loggingOut}
                className="flex items-center gap-1 rounded-lg p-1.5 transition-colors hover:bg-slate-50"
              >
                <span className={`flex h-8 w-8 items-center justify-center rounded-full text-white shadow-sm ${loginType === 'master' ? 'bg-gradient-to-r from-red-600 to-rose-700' : 'bg-gradient-to-r from-pr-navy-500 to-pr-navy-700'}`}>
                  {loginType === 'master' ? <Shield className="h-4 w-4" /> : <User className="h-4 w-4" />}
                </span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {userDropdownOpen && !loggingOut && (
                <div className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-xl ring-1 ring-black/5">
                  <div className="border-b border-slate-50 bg-slate-50/50 p-4">
                    <p className="truncate text-sm font-semibold text-slate-800">{email}</p>
                    <p className="text-xs text-slate-500">{userTypeLabel}</p>
                  </div>
                  <div className="p-1.5">
                    <button onClick={handleLogout} className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-pr-navy-700">
                      <LogOut className="mr-3 h-4 w-4 text-slate-400" /> Cerrar Sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setMobileMenuOpen((o) => !o)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile tabs */}
        {mobileMenuOpen && (
          <div className="border-t border-slate-100 py-3 md:hidden">
            <div className="space-y-1">
              {NAV_ITEMS.map((item) => {
                const active = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => selectTab(item.id)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition-all ${
                      active ? 'bg-pr-navy-50 text-pr-navy-700 ring-1 ring-pr-navy-100' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className={active ? 'text-pr-navy-600' : 'text-slate-400'}>{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
